type ValueOf<T> = T[keyof T];

export const GroupName = {
  Account: 'Account',
  Loyalty: 'Loyalty',
  Collection: 'Collection',
  Customer: 'Customer',
  Product: 'Product',
  Cart: 'Cart',
  CheckoutStandard: 'Checkout Standard',
  CheckoutExpress: 'Checkout Express',
  CheckoutWallet: 'Checkout Wallet',
  CheckoutQuestionnaire: 'Checkout Questionnaire',
  Shipping: 'Shipping',
  PromoBar: 'Promo Bar',
  PromoHeader: 'Promo Header',
  AdTile: 'Ad Tile',
  Debug: 'Debug',
  Form: 'Form',
  Consent: 'Consent',
  Story: 'Story',
  FuBe: 'FuBe',
  ConversationalWidget: 'Conversational Widget',
  Subscription: 'Subscription',
  Component: 'Component',
  HomePromoTile: 'Home Promo Tile',
  PickyCats: 'Picky Cats',
} as const;

export type GroupName = ValueOf<typeof GroupName>;

export const StandaloneEventName = {
  PageView: 'pageView',
  SelectElement: 'Select Element',
  UnselectElement: 'Unselect Element',
  ViewElement: 'View Element',
  ScrollElement: 'Scroll Element',
  ZoomElement: 'Zoom Element',

  // Legacy events; moved from utils/tracking.ts
  CookieConsentAccepted: 'AcceptedCookieConsent',
  CookieConsentDismissed: 'DismissedCookieConsent',
  CookieConsentDisplay: 'DisplayCookieConsent',
  CountryModalClicked: 'ClickedShopCountryModal',
  CountryModalDismissed: 'DismissedShopCountryModal',
  NextJsMetric: 'NextJS Metric',
  NewsletterSignup: 'newsletterSignup',
  ReorderDetail: 'ReorderDetail',
  ReorderMyAccount: 'ReorderMyAccount',
  SearchStart: 'SearchStart',
  WebVitals: 'Web Vitals',
} as const;

export type StandaloneEventName = ValueOf<typeof StandaloneEventName>;

export const SessionEventName = {
  SetSession: 'Set Session',
} as const;

export type SessionEventName = ValueOf<typeof SessionEventName>;

export const CommonEventName = {
  Click: 'Click',
  Impression: 'Impression',
  Submit: 'Submit',
  Ready: 'Ready',
  View: 'View',
  Success: 'Success',
} as const;

export type CommonEventName = ValueOf<typeof CommonEventName>;

export const AccountEventName = {
  Login: 'login',
  Logout: 'logout',
} as const;

export type AccountEventName = ValueOf<typeof AccountEventName>;

export const CartEventName = {
  ReadyCart: 'Ready Cart',
  CheckoutOptionSelect: 'Select Checkout Method',
  OpenSubscriptionOptions: 'Open Subscription Options',
  SelectIntervalOption: 'Select Interval Option',
} as const;

export type CartEventName = ValueOf<typeof CartEventName>;

export const CheckoutEventName = {
  CheckoutLoaded: 'Checkout Loaded',
  ReadyUserRole: 'Ready User Role',
  SelectUserRole: 'Select User Role',
  SubmitUserRole: 'Submit User Role',
  UserIdentified: 'User Identified',
  ReadyAddresses: 'Ready Addresses',
  SubmitAddresses: 'Submit Addresses',
  ReadyShipping: 'Ready Shipping',
  SelectShippingOption: 'Select Shipping Method',
  SubmitShipping: 'Submit Shipping',
  ReadyPayment: 'Ready Payment',
  SelectPaymentOption: 'Select Payment Option',
  SubmitPayment: 'Submit Payment',
  ReadyConfirmation: 'Ready Confirmation',
  Purchase: 'Purchase',
} as const;

export type CheckoutEventName = ValueOf<typeof CheckoutEventName>;

export const CheckoutExpressEventName = {
  ReadySummary: 'Ready Summary',
  ReadyConfirmation: 'Ready Confirmation',
  SelectShippingOption: 'Select Shipping Method',
  SubmitOrder: 'Submit Order',
  Purchase: 'Purchase',
  Confirm: 'Confirm',
} as const;

export type CheckoutExpressEventName = ValueOf<typeof CheckoutExpressEventName>;

export const CheckoutWalletEventName = {
  SelectShippingOption: 'Select Shipping Method',
  SelectShippingAddress: 'Select Shipping Address',
  PaymentSuccess: 'Payment Success',
  OrderSuccess: 'Order Success',
} as const;

export type CheckoutWalletEventName = ValueOf<typeof CheckoutWalletEventName>;

export const CheckoutQuestionnaireEventName = {
  View: 'View',
  Start: 'Start',
  End: 'End',
  Answer: 'Answer',
} as const;

export type CheckoutQuestionnaireEventName = ValueOf<typeof CheckoutQuestionnaireEventName>;

export const CollectionEventName = {
  FilterOptionsOpen: 'Filter Options Open',
  FilterOptionsConfirm: 'Filter Options Confirm',
  FilterOptionsCancel: 'Filter Options Cancel',
  FilterOptionsClear: 'Filter Options Clear',
  FilterOptionsReset: 'Filter Options Reset',
  FilterOptionsClick: 'Filter Options Click',
  SortOptionsOpen: 'Sort Options Open',
  SortOptionsSelect: 'Sort Options Select',
} as const;

export type CollectionEventName = ValueOf<typeof CollectionEventName>;

export const ComponentEventName = {
  Impression: 'Impression',
  Click: 'Click',
} as const;

export type ComponentEventName = ValueOf<typeof ComponentEventName>;

export const ConsentEventName = {
  Ok: 'ok',
  Settings: 'einstellungen',
  AcceptAll: 'alle akzeptieren',
  ConfirmSelection: 'auswahl bestatigen',
} as const;

export type ConsentEventName = ValueOf<typeof ConsentEventName>;

export const FuBeEventName = {
  Question: 'Question',
  Answer: 'Answer',
} as const;

export type FuBeEventName = ValueOf<typeof FuBeEventName>;

export const LoyaltyEventName = {
  RedeemOptionView: 'Redeem Option View',
  Redeem: 'Redeem',
} as const;

export type LoyaltyEventName = ValueOf<typeof LoyaltyEventName>;

export const ProductEventName = {
  Impressions: 'Impressions',
  Click: 'Click',
  Details: 'Details',
  SwitchVariant: 'Switch Variant',
  AddToCart: 'Add To Cart',
  InfoTabClick: 'InfoTabClick',
  ClickAdventCalendar: 'Click Advent Calendar',
} as const;

export type ProductEventName = ValueOf<typeof ProductEventName>;

export const PromoBarEventName = {
  SubmitCouponCode: 'Submit Coupon Code',
} as const;

export type PromoBarEventName = ValueOf<typeof PromoBarEventName>;

export const PickyCatsEventName = {
  JourneyEntry: 'Journey Entry',
  JourneyAnswer: 'Journey Answer',
  JourneyResult: 'Journey Result',
  JourneyHandoff: 'Journey Handoff',
  JourneyExit: 'Journey Exit',
  CartAction: 'Cart Action',
} as const;

export type PickyCatsEventName = ValueOf<typeof PickyCatsEventName>;

export const StoryEventName = {
  Impressions: 'Impressions',
  Views: 'Views',
  Next: 'Next',
  Previous: 'Previous',
  Exit: 'Exit',
} as const;

export type StoryEventName = ValueOf<typeof StoryEventName>;

export const SubscriptionEventName = {
  MoveOptionsView: 'Move Options View',
  MoveOptionsConfirm: 'Move Options Confirm',
  PauseOptionsView: 'Pause Options View',
  PauseOptionsPauseSelect: 'Pause Options Pause Select',
  PauseOptionsPauseConfirm: 'Pause Options Pause Confirm',
  PauseOptionsMoveConfirm: 'Pause Options Move Confirm',
  CancelOptionsView: 'Cancel Options View',
  CancelOptionsConfirm: 'Cancel Options Confirm',
  Restart: 'Restart',
  AddItem: 'Add Item',
  RemoveItem: 'Remove Item',
  RestoreItem: 'Restore Item',
  IncreaseItemQuantity: 'Increase Item Quantity',
  DecreaseItemQuantity: 'Decrease Item Quantity',
  SelectFilter: 'Select Filter',
  IntervalSelect: 'Interval Select',
  OrderTypeSelect: 'Order Type Select',
  AddToSubscriptionClick: 'AddToSubscriptionClick',
  UpsellListView: 'Upsell List View',
  UpsellListSelect: 'Upsell List Select',
  UpsellListClickItem: 'Upsell List Click Item',
  UpsellListModalView: 'Upsell List Modal View',
  UpsellListAddItem: 'Upsell List Add Item',
} as const;

export type SubscriptionEventName = ValueOf<typeof SubscriptionEventName>;
